import { useCallback, useEffect, useRef, useState } from 'react';
import { useAnalytics } from './useAnalytics';
import { AnswerType, GameSessionType, QuestionType } from '../types/entities';

const INTER_QUESTION_DELAY_MS = 3000;

export const TIME_TO_ANSWER = 30;

type UseQuestionFlowOptions = {
  currentQuestion?: QuestionType;
  currentIndex: number;
  questionCount: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  updateAnswer: (answerId: number, timeInSeconds?: number) => Promise<void>;
  onComplete: () => void;
  gameSession?: GameSessionType;
  trackAnswerTime?: boolean;
  resetIndexOnMount?: boolean;
  isReady?: boolean;
  onNotReady?: () => void;
  timeLimitSeconds?: number;
};

export const useQuestionFlow = ({
  currentQuestion,
  currentIndex,
  questionCount,
  setCurrentIndex,
  updateAnswer,
  onComplete,
  gameSession,
  trackAnswerTime = false,
  resetIndexOnMount = false,
  isReady,
  onNotReady,
  timeLimitSeconds = TIME_TO_ANSWER,
}: UseQuestionFlowOptions) => {
  const { onQuestionShown, onQuestionAnswered, onTimeout } = useAnalytics();

  const currentIndexRef = useRef(currentIndex);
  const questionCountRef = useRef(questionCount);
  const questionStartDateRef = useRef(new Date());
  const answering = useRef(false);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const answerTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const timeLimitSecondsRef = useRef(timeLimitSeconds);
  const nextQuestionRef = useRef<() => void>(() => undefined);

  const [loadingNewQuestion, setLoadingNewQuestion] = useState(false);
  const [questionStartDate, setQuestionStartDate] = useState(() => new Date());
  const loadingNewQuestionRef = useRef(loadingNewQuestion);

  const updateLoadingNewQuestion = useCallback((value: boolean) => {
    setLoadingNewQuestion(value);
    loadingNewQuestionRef.current = value;
  }, []);

  const clearAnswerTimeout = useCallback(() => {
    if (answerTimeoutId.current) {
      clearTimeout(answerTimeoutId.current);
      answerTimeoutId.current = null;
    }
  }, []);

  const armAnswerTimeout = useCallback(
    (question: QuestionType) => {
      clearAnswerTimeout();
      const limit = timeLimitSecondsRef.current;
      if (!limit || loadingNewQuestionRef.current) return;

      const startDate = new Date();
      questionStartDateRef.current = startDate;
      setQuestionStartDate(startDate);

      answerTimeoutId.current = setTimeout(() => {
        if (answering.current || loadingNewQuestionRef.current) return;
        answering.current = true;
        updateLoadingNewQuestion(true);
        onTimeout(question);
        nextQuestionRef.current();
      }, limit * 1000);
    },
    [clearAnswerTimeout, updateLoadingNewQuestion, onTimeout],
  );

  const ready = isReady ?? true;

  useEffect(() => {
    timeLimitSecondsRef.current = timeLimitSeconds;
  }, [timeLimitSeconds]);

  useEffect(() => {
    questionCountRef.current = questionCount;
  }, [questionCount]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    if (!resetIndexOnMount) return;
    setCurrentIndex(0);
    currentIndexRef.current = 0;
  }, [resetIndexOnMount, setCurrentIndex]);

  useEffect(() => {
    if (!ready || questionCount > 0 || !onNotReady) return;
    onNotReady();
  }, [ready, questionCount, onNotReady]);

  useEffect(() => {
    if (!currentQuestion) return;
    onQuestionShown(currentQuestion);
    if (!loadingNewQuestionRef.current) {
      armAnswerTimeout(currentQuestion);
    }
  }, [currentQuestion?.documentId, onQuestionShown, currentQuestion, armAnswerTimeout]);

  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      clearAnswerTimeout();
    };
  }, [clearAnswerTimeout]);

  const nextQuestion = useCallback(() => {
    const nextQuestionNumber = currentIndexRef.current + 1;
    const isLastQuestion = nextQuestionNumber >= questionCountRef.current;

    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    clearAnswerTimeout();

    timeoutId.current = setTimeout(() => {
      updateLoadingNewQuestion(false);
      if (isLastQuestion) {
        answering.current = false;
        onComplete();
      } else {
        setCurrentIndex((prev) => {
          const nextIndex = prev + 1;
          currentIndexRef.current = nextIndex;
          return nextIndex;
        });
        answering.current = false;
      }
    }, INTER_QUESTION_DELAY_MS);
  }, [setCurrentIndex, updateLoadingNewQuestion, onComplete, clearAnswerTimeout]);

  useEffect(() => {
    nextQuestionRef.current = nextQuestion;
  }, [nextQuestion]);

  const onSelectedAnswer = useCallback(
    async (option: AnswerType) => {
      if (answering.current || loadingNewQuestionRef.current) {
        return;
      }
      answering.current = true;
      clearAnswerTimeout();
      try {
        const timeInSeconds = trackAnswerTime
          ? Math.trunc((Date.now() - questionStartDateRef.current.getTime()) / 1000)
          : undefined;
        await updateAnswer(option.id, timeInSeconds);
        if (currentQuestion) {
          onQuestionAnswered(currentQuestion, option);
        }
        updateLoadingNewQuestion(true);
        nextQuestion();
      } catch {
        answering.current = false;
        if (currentQuestion) {
          armAnswerTimeout(currentQuestion);
        }
      }
    },
    [
      currentQuestion,
      nextQuestion,
      onQuestionAnswered,
      trackAnswerTime,
      updateAnswer,
      updateLoadingNewQuestion,
      clearAnswerTimeout,
      armAnswerTimeout,
    ],
  );

  const skipQuestion = useCallback(() => {
    if (answering.current || loadingNewQuestionRef.current) {
      return;
    }
    answering.current = true;
    clearAnswerTimeout();
    updateLoadingNewQuestion(true);
    nextQuestion();
  }, [clearAnswerTimeout, updateLoadingNewQuestion, nextQuestion]);

  const endFlow = useCallback(() => {
    answering.current = false;
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    clearAnswerTimeout();
  }, [clearAnswerTimeout]);

  const currentAnsweredQuestion = gameSession?.questionsAnswered?.find(
    (ans) => ans.question.documentId === currentQuestion?.documentId,
  );

  const hasNextQuestion = currentIndex + 1 < questionCount;
  const isRenderable = ready && questionCount > 0;

  return {
    loadingNewQuestion,
    onSelectedAnswer,
    skipQuestion,
    currentAnsweredQuestion,
    hasNextQuestion,
    isRenderable,
    questionStartDate,
    endFlow,
  };
};
